import { useCallback, useEffect, useRef, useState } from 'react'
import type { CSSProperties, PointerEvent as ReactPointerEvent } from 'react'
import { useSetting } from './useSetting'

export interface PanelFrameSetting {
  detached: boolean
  x: number
  y: number
  height: number | null
}

const DEFAULT_FRAME: PanelFrameSetting = { detached: false, x: 80, y: 80, height: null }
const DRAG_THRESHOLD_PX = 4
const DESKTOP_MQ = '(min-width: 1024px)'
const INTERACTIVE_SELECTOR =
  'button, input, select, textarea, a, label, [role="button"], [role="tab"]'

function isDesktopNow() {
  return typeof window !== 'undefined' && window.matchMedia(DESKTOP_MQ).matches
}

/**
 * Detach/drag/resize/dock state for a desktop floating panel. Docked panels
 * keep their CSS-positioned spot; dragging the header detaches them into a
 * free-floating window (position + height persisted via settings), and
 * dock() snaps them back. Live drag positions stay in local state — only
 * the final frame on pointer-up is written through to the server.
 *
 * Desktop-only: drags no-op below the lg breakpoint and a persisted
 * detached frame is ignored there, so mobile layouts are untouched.
 */
export function usePanelFrame(settingKey: string, { minHeight = 220 } = {}) {
  const [saved, setSaved] = useSetting<PanelFrameSetting>(settingKey, DEFAULT_FRAME)
  const [live, setLive] = useState<PanelFrameSetting | null>(null)
  const [isDesktop, setIsDesktop] = useState(isDesktopNow)
  const ref = useRef<HTMLDivElement | null>(null)
  const didDragRef = useRef(false)
  const savedRef = useRef(saved)
  savedRef.current = saved
  const liveRef = useRef(live)
  liveRef.current = live

  useEffect(() => {
    const mq = window.matchMedia(DESKTOP_MQ)
    const onChange = () => setIsDesktop(mq.matches)
    mq.addEventListener('change', onChange)
    return () => mq.removeEventListener('change', onChange)
  }, [])

  const frame = live ?? saved
  const detached = frame.detached && isDesktop

  const commit = useCallback(
    (next: PanelFrameSetting) => {
      setSaved(next)
      setLive(null)
    },
    [setSaved],
  )

  const trackPointer = useCallback(
    (onMove: (ev: PointerEvent) => PanelFrameSetting | null) => {
      let last: PanelFrameSetting | null = null
      const move = (ev: PointerEvent) => {
        const next = onMove(ev)
        if (!next) return
        last = next
        document.body.style.userSelect = 'none'
        setLive(next)
      }
      const up = () => {
        window.removeEventListener('pointermove', move)
        window.removeEventListener('pointerup', up)
        document.body.style.userSelect = ''
        if (last) commit(last)
        // Click events fire after pointerup; let handlers consume the
        // did-drag flag synchronously, then clear it.
        setTimeout(() => {
          didDragRef.current = false
        }, 0)
      }
      window.addEventListener('pointermove', move)
      window.addEventListener('pointerup', up)
    },
    [commit],
  )

  /** Attach to the panel header. Drag past the threshold detaches + moves. */
  const onHeaderPointerDown = useCallback(
    (e: ReactPointerEvent) => {
      if (!isDesktopNow()) return
      if ((e.target as HTMLElement).closest(INTERACTIVE_SELECTOR)) return
      const el = ref.current
      if (!el) return
      const rect = el.getBoundingClientRect()
      const startX = e.clientX
      const startY = e.clientY
      const height = (liveRef.current ?? savedRef.current).height
      let dragging = false
      trackPointer((ev) => {
        const dx = ev.clientX - startX
        const dy = ev.clientY - startY
        if (!dragging && Math.hypot(dx, dy) < DRAG_THRESHOLD_PX) return null
        dragging = true
        didDragRef.current = true
        return {
          detached: true,
          x: Math.round(rect.left + dx),
          y: Math.round(rect.top + dy),
          height,
        }
      })
    },
    [trackPointer],
  )

  /** Attach to the bottom resize handle (rendered only while detached). */
  const onResizePointerDown = useCallback(
    (e: ReactPointerEvent) => {
      if (!isDesktopNow()) return
      e.preventDefault()
      const el = ref.current
      if (!el) return
      const rect = el.getBoundingClientRect()
      const startY = e.clientY
      const base = liveRef.current ?? savedRef.current
      trackPointer((ev) => {
        didDragRef.current = true
        const h = Math.max(
          minHeight,
          Math.min(window.innerHeight - rect.top - 8, rect.height + (ev.clientY - startY)),
        )
        return { ...base, detached: true, height: Math.round(h) }
      })
    },
    [trackPointer, minHeight],
  )

  const dock = useCallback(() => {
    commit({ ...(liveRef.current ?? savedRef.current), detached: false })
  }, [commit])

  /** True (and consumed) when the pointer interaction was a drag — call from
   * click handlers to skip click behavior that follows a drag. */
  const consumeDragClick = useCallback(() => {
    const was = didDragRef.current
    didDragRef.current = false
    return was
  }, [])

  /** Inline style while detached; `undefined` when docked (CSS positions it).
   * CSS min() keeps the panel reachable if the window shrinks. */
  const style = (applyHeight: boolean): CSSProperties | undefined => {
    if (!detached) return undefined
    const s: CSSProperties = {
      left: `min(${frame.x}px, calc(100vw - 160px))`,
      top: `min(${Math.max(0, frame.y)}px, calc(100vh - 48px))`,
      right: 'auto',
      bottom: 'auto',
    }
    if (applyHeight && frame.height != null) {
      s.height = frame.height
      s.minHeight = 0
      s.maxHeight = 'none'
    }
    return s
  }

  return {
    ref,
    detached,
    height: detached ? frame.height : null,
    style,
    onHeaderPointerDown,
    onResizePointerDown,
    dock,
    consumeDragClick,
  }
}
